import roughjs from 'roughjs';
import { Point } from 'roughjs/bin/geometry';
import { RoughSVG } from 'roughjs/bin/svg';

import { angleOfLine, pointAtAngleAndDistance, toRadians } from '@cast/utils';

import {
  ARROW_HEAD_ANGLE,
  ARROW_HEAD_LEFT_WING_SIZE,
  ARROW_HEAD_RIGHT_WING_SIZE,
  BEND_FACTOR,
} from './constants';

export function findClosestSide(
  fromElement: DOMRect,
  toElement: DOMRect
): Side {
  const fromCenterX = fromElement.x + fromElement.width / 2;
  const fromCenterY = fromElement.y + fromElement.height / 2;

  if (fromCenterY > toElement.y + toElement.height) {
    return 'bottom';
  }

  if (fromCenterY < toElement.y) {
    return 'top';
  }

  if (fromCenterX < toElement.x) {
    return 'left';
  }

  return 'right';
}

export type Side = 'top' | 'bottom' | 'left' | 'right';

export function calcCenterSidePosition(
  elementRect: DOMRect,
  side: Side
): DOMRect {
  switch (side) {
    case 'top':
      return new DOMRect(
        elementRect.x + elementRect.width / 2,
        elementRect.y,
        0,
        0
      );
    case 'bottom':
      return new DOMRect(
        elementRect.x + elementRect.width / 2,
        elementRect.y + elementRect.height,
        0,
        0
      );
    case 'left':
      return new DOMRect(
        elementRect.x,
        elementRect.y + elementRect.height / 2,
        0,
        0
      );
    case 'right':
      return new DOMRect(
        elementRect.x + elementRect.width,
        elementRect.y + elementRect.height / 2,
        0,
        0
      );
    default: {
      const _: never = side;
      return _;
    }
  }
}

export function getStartEndPositions(
  fromElement: HTMLElement,
  toElement: HTMLElement
): [DOMRect, DOMRect] {
  const fromPosition = fromElement.getBoundingClientRect();
  const toPosition = toElement.getBoundingClientRect();

  // always from center
  const fromElementCenterX = fromPosition.x + fromPosition.width / 2;
  const fromElementCenterY = fromPosition.y + fromPosition.height / 2;

  // to the center of the closest side
  const toSide = findClosestSide(fromPosition, toPosition);
  const toSidePosition = calcCenterSidePosition(toPosition, toSide);

  return [
    new DOMRect(fromElementCenterX, fromElementCenterY, 0, 0),
    toSidePosition,
  ];
}

export function getLinePoints(fromRect: DOMRect, toRect: DOMRect): Point[] {
  const startPoint: Point = [fromRect.x, fromRect.y];
  const endPoint: Point = [toRect.x, toRect.y];

  const bendXDirection = toRect.x > fromRect.x ? 1 : -1;
  const bendYDirection = toRect.y > fromRect.y ? -1 : 1;
  const halfDistanceX = (toRect.x - fromRect.x) / 2;
  const halfDistanceY = (toRect.y - fromRect.y) / 2;
  // the bend point is a center point moved into a proper direction
  const bendPoint: Point = [
    fromRect.x +
      halfDistanceX +
      bendXDirection * Math.abs(halfDistanceX) * BEND_FACTOR,
    fromRect.y +
      halfDistanceY +
      bendYDirection * Math.abs(halfDistanceY) * BEND_FACTOR,
  ];

  return [startPoint, bendPoint, endPoint];
}

export function getArrowHeadPoints(linePoints: Point[]): Point[] {
  // Calculate the angle of the original line's final segment
  const endPoint = linePoints[2];
  const bendPoint = linePoints[1];
  const lineAngle =
    angleOfLine(bendPoint, endPoint) +
    toRadians(-180); /* arrows will point into the opposite direction */
  const leftWingAngle = lineAngle + toRadians(ARROW_HEAD_ANGLE / 2);
  const rightWingAngle = lineAngle - toRadians(ARROW_HEAD_ANGLE / 2);

  return [
    pointAtAngleAndDistance(endPoint, leftWingAngle, ARROW_HEAD_LEFT_WING_SIZE),
    endPoint,
    pointAtAngleAndDistance(
      endPoint,
      rightWingAngle,
      ARROW_HEAD_RIGHT_WING_SIZE
    ),
  ];
}

export function createStableRenderer(svg: SVGSVGElement): RoughSVG {
  return roughjs.svg(svg, { options: { seed: new Date().getTime() } });
}

export function drawArrow(
  svg: RoughSVG,
  points: Point[],
  color: string
): SVGGElement {
  return svg.curve(points, {
    stroke: color,
    strokeWidth: 3,
    bowing: 2,
    roughness: 1.5,
  });
}

export function drawArrowHead(
  svg: RoughSVG,
  points: Point[],
  color: string
): SVGGElement {
  return svg.linearPath(points, {
    stroke: color,
    strokeWidth: 3,
    bowing: 2,
    roughness: 2,
  });
}
